import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

import { HoverHintTrigger } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  computeAnchoredListPanel,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredListPanelBox,
} from "@/lib/viewportAnchoredPopover";

import { fetchSalesOrderShipByCalendarFullSchedule } from "./salesOrderShipByCalendarApi";
import { getSgsHolidayName } from "./sgs-holidays";
import {
  maxSerializedUnitsInMonth,
  shipByHeatmapCellClass,
  shipByHeatmapLevel,
  SHIP_BY_HOLIDAY_HATCH_CLASS,
  SHIP_BY_WEEK_COLUMN_HEADER_HINT,
  SHIP_BY_WEEK_TOTAL_COLUMN_HINT,
  shipByDayCellHoverHint,
  sumSerializedUnitsForIsoDates,
} from "./shipByDatePickerHeatmap";
import {
  chicagoTodayIsoDate,
  formatUsDateFromIso,
  monthMatrix,
  monthMatrixWeekRows,
  parseIsoDate,
  SHIP_BY_WEEK_SHORTCUTS,
  shipByDateFromWeeksFromToday,
} from "./shipByDatePickerUtils";

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;
const PANEL_GAP_PX = 6;
const VIEWPORT_EDGE_PX = 8;
const PANEL_MIN_WIDTH_PX = 400;

export interface ShipByDatePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  showWeekShortcuts?: boolean;
}

function todayIsoDate(): string {
  return chicagoTodayIsoDate();
}

export function ShipByDatePicker({
  id,
  value,
  onChange,
  disabled = false,
  className,
  showWeekShortcuts = true,
}: ShipByDatePickerProps): JSX.Element {
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [panelBox, setPanelBox] = useState<AnchoredListPanelBox | null>(null);
  const [visibleMonth, setVisibleMonth] = useState(() => parseIsoDate(value) ?? new Date());
  const [serializedUnitsByDate, setSerializedUnitsByDate] = useState<Record<string, number>>({});
  const [calendarLoadError, setCalendarLoadError] = useState<string | null>(null);
  const [calendarLoadPending, setCalendarLoadPending] = useState(false);

  const displayValue = useMemo(() => formatUsDateFromIso(value), [value]);
  const weekShortcuts = showWeekShortcuts ? SHIP_BY_WEEK_SHORTCUTS : [];
  const weekShortcutTargets = useMemo(
    () =>
      weekShortcuts.map((weeks) => {
        const isoDate = shipByDateFromWeeksFromToday(weeks);
        const holidayName = getSgsHolidayName(isoDate);
        return { weeks, isoDate, holidayName };
      }),
    [weekShortcuts],
  );

  const visibleYear = visibleMonth.getFullYear();
  const visibleMonthIndex = visibleMonth.getMonth();
  const monthCells = useMemo(() => monthMatrix(visibleYear, visibleMonthIndex), [visibleYear, visibleMonthIndex]);
  const monthWeekRows = useMemo(
    () => monthMatrixWeekRows(visibleYear, visibleMonthIndex),
    [visibleYear, visibleMonthIndex],
  );
  const inMonthIsoDates = useMemo(
    () => monthCells.filter((cell) => cell.inMonth).map((cell) => cell.isoDate),
    [monthCells],
  );
  const heatmapMaxUnits = useMemo(
    () => maxSerializedUnitsInMonth(serializedUnitsByDate, inMonthIsoDates),
    [serializedUnitsByDate, inMonthIsoDates],
  );

  useEffect(() => {
    if (!open) {
      const parsed = parseIsoDate(value);
      if (parsed != null) {
        setVisibleMonth(parsed);
      }
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) {
      setSerializedUnitsByDate({});
      setCalendarLoadError(null);
      setCalendarLoadPending(false);
      return;
    }
    let cancelled = false;
    setCalendarLoadError(null);
    setCalendarLoadPending(true);
    void fetchSalesOrderShipByCalendarFullSchedule()
      .then((response) => {
        if (!cancelled) {
          setSerializedUnitsByDate(response.serializedUnitsByDate ?? {});
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setSerializedUnitsByDate({});
          setCalendarLoadError(error instanceof Error ? error.message : "Could not load shipping load.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCalendarLoadPending(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const repositionPanel = () => {
    const anchor = triggerRef.current;
    if (anchor == null) {
      return;
    }
    setPanelBox(
      computeAnchoredListPanel({
        anchorRect: anchor.getBoundingClientRect(),
        gap: PANEL_GAP_PX,
        edge: VIEWPORT_EDGE_PX,
        minWidth: PANEL_MIN_WIDTH_PX,
        chromeHeight: 0,
        listMaxHeightCap: 460,
      }),
    );
  };

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    repositionPanel();
    const onResize = () => repositionPanel();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, weekShortcuts.length]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const commitDate = (isoDate: string) => {
    onChange(isoDate);
    setOpen(false);
  };

  const pickWeekShortcut = (weeks: number) => {
    commitDate(shipByDateFromWeeksFromToday(weeks));
  };

  const monthLabel = visibleMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <>
      <div className={cn("relative", className)}>
        <button
          ref={triggerRef}
          id={id}
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => {
            if (disabled) {
              return;
            }
            setOpen((current) => !current);
          }}
          className={cn(
            "flex h-10 w-full items-center rounded-md border border-slate-300 bg-white px-3 pr-10 text-left text-sm text-slate-900",
            "focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
            "disabled:bg-slate-100 disabled:text-slate-500",
            !displayValue && "text-slate-400",
          )}
        >
          <span className="tabular-nums">{displayValue || "MM/DD/YYYY"}</span>
        </button>
        <CalendarDays
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
          aria-hidden="true"
        />
      </div>

      {open && panelBox != null
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              aria-label="Ship by date"
              className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
              style={{
                position: "fixed",
                top: panelBox.top,
                left: panelBox.left,
                width: Math.max(panelBox.width, PANEL_MIN_WIDTH_PX),
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
            >
              <div className="flex">
                <div className="min-w-0 flex-1 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <ButtonIcon
                      label="Previous month"
                      onClick={() =>
                        setVisibleMonth(
                          (current) => new Date(current.getFullYear(), current.getMonth() - 1, 1, 12, 0, 0, 0),
                        )
                      }
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden />
                    </ButtonIcon>
                    <span className="text-sm font-medium text-slate-900">{monthLabel}</span>
                    <ButtonIcon
                      label="Next month"
                      onClick={() =>
                        setVisibleMonth(
                          (current) => new Date(current.getFullYear(), current.getMonth() + 1, 1, 12, 0, 0, 0),
                        )
                      }
                    >
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </ButtonIcon>
                  </div>
                  <div className="mt-3 grid grid-cols-[repeat(7,minmax(0,1fr))_2.75rem] gap-1 text-center text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    {WEEKDAY_LABELS.map((label) => (
                      <span key={label} className="py-1">
                        {label}
                      </span>
                    ))}
                    <HoverHintTrigger
                      align="center"
                      trigger={
                        <span className="py-1 text-[10px] normal-case tracking-normal" tabIndex={0}>
                          Wk
                        </span>
                      }
                    >
                      {SHIP_BY_WEEK_COLUMN_HEADER_HINT}
                    </HoverHintTrigger>
                  </div>
                  <div className="mt-1 flex flex-col gap-1">
                    {monthWeekRows.map((weekRow) => {
                      const weekTotal = sumSerializedUnitsForIsoDates(
                        weekRow.map((cell) => cell.isoDate),
                        serializedUnitsByDate,
                      );
                      return (
                        <div
                          key={weekRow[0]?.isoDate ?? "week"}
                          className="grid grid-cols-[repeat(7,minmax(0,1fr))_2.75rem] gap-1"
                        >
                          {weekRow.map((cell) => {
                            const parsed = parseIsoDate(cell.isoDate);
                            const dayNumber = parsed?.getDate() ?? "";
                            const isSelected = cell.isoDate === value;
                            const isToday = cell.isoDate === todayIsoDate();
                            const isBeforeToday = cell.isoDate < todayIsoDate() && !isSelected;
                            const holidayName = cell.inMonth ? getSgsHolidayName(cell.isoDate) : null;
                            const isWeekend = parsed != null && (parsed.getDay() === 0 || parsed.getDay() === 6);
                            const dayUnits = cell.inMonth ? (serializedUnitsByDate[cell.isoDate] ?? 0) : 0;
                            const heatmapLevel = cell.inMonth
                              ? shipByHeatmapLevel(dayUnits, heatmapMaxUnits)
                              : 0;
                            const heatmapClass =
                              cell.inMonth
                                ? shipByHeatmapCellClass(heatmapLevel, {
                                    selected: isSelected,
                                  })
                                : "";
                            const dayHoverHint = cell.inMonth
                              ? shipByDayCellHoverHint({
                                  isoDate: cell.isoDate,
                                  dayUnits,
                                  holidayName,
                                  formatUsDate: formatUsDateFromIso,
                                })
                              : null;
                            const dayButton = (
                              <button
                                type="button"
                                disabled={isBeforeToday}
                                onClick={() => {
                                  if (isBeforeToday) {
                                    return;
                                  }
                                  commitDate(cell.isoDate);
                                }}
                                className={cn(
                                  "relative h-8 w-full rounded-md text-sm tabular-nums transition-colors",
                                  isBeforeToday && "cursor-not-allowed text-slate-300",
                                  !cell.inMonth && "text-slate-300",
                                  cell.inMonth &&
                                    !isSelected &&
                                    holidayName == null &&
                                    isWeekend &&
                                    heatmapLevel === 0 &&
                                    "bg-slate-50 text-slate-600 hover:bg-slate-100",
                                  cell.inMonth &&
                                    !isSelected &&
                                    holidayName == null &&
                                    !isWeekend &&
                                    heatmapLevel === 0 &&
                                    "text-slate-700 hover:bg-slate-100",
                                  cell.inMonth && !isSelected && heatmapClass,
                                  cell.inMonth &&
                                    !isSelected &&
                                    holidayName != null &&
                                    "text-amber-950 ring-1 ring-inset ring-amber-200/80",
                                  isSelected && "bg-brand-600 font-semibold text-white hover:bg-brand-700",
                                  isToday && !isSelected && "ring-1 ring-brand-300",
                                )}
                              >
                                {dayNumber}
                                {holidayName != null && !isSelected ? (
                                  <span
                                    className={cn(
                                      "pointer-events-none absolute inset-0 rounded-md",
                                      SHIP_BY_HOLIDAY_HATCH_CLASS,
                                    )}
                                    aria-hidden="true"
                                  />
                                ) : null}
                              </button>
                            );
                            return dayHoverHint != null ? (
                              <HoverHintTrigger key={cell.isoDate} align="center" block trigger={dayButton}>
                                {dayHoverHint}
                              </HoverHintTrigger>
                            ) : (
                              <span key={cell.isoDate} className="contents">{dayButton}</span>
                            );
                          })}
                          <HoverHintTrigger
                            align="center"
                            block
                            trigger={
                              <div
                                className="flex h-8 items-center justify-center rounded-md bg-slate-50 text-[10px] font-medium tabular-nums text-slate-600"
                                tabIndex={0}
                              >
                                {weekTotal > 0 ? numberFormatter.format(weekTotal) : "—"}
                              </div>
                            }
                          >
                            {SHIP_BY_WEEK_TOTAL_COLUMN_HINT}
                          </HoverHintTrigger>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className={cn("relative h-3 w-3 rounded-sm ring-1 ring-inset ring-amber-200/80", SHIP_BY_HOLIDAY_HATCH_CLASS)}
                        aria-hidden="true"
                      />
                      SGS holiday
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-3 w-3 rounded-sm bg-slate-50" aria-hidden="true" />
                      Weekend
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-3 w-3 rounded-sm bg-red-200" aria-hidden="true" />
                      Serialized ship-by load
                    </span>
                  </div>
                  {calendarLoadPending ? (
                    <p className="mt-1 text-[11px] text-slate-500">Loading ship-by load…</p>
                  ) : null}
                  {calendarLoadError != null ? (
                    <p className="mt-1 text-[11px] text-amber-800">{calendarLoadError}</p>
                  ) : null}
                  <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2">
                    <button
                      type="button"
                      className="text-sm font-medium text-brand-600 hover:text-brand-700"
                      onClick={() => commitDate("")}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="text-sm font-medium text-brand-600 hover:text-brand-700"
                      onClick={() => commitDate(todayIsoDate())}
                    >
                      Today
                    </button>
                  </div>
                </div>
                {weekShortcuts.length > 0 ? (
                  <div className="flex w-[7.25rem] shrink-0 flex-col gap-1 border-l border-slate-200 bg-slate-50 p-2">
                    {weekShortcutTargets.map((shortcut) => {
                      const datePreview = formatUsDateFromIso(shortcut.isoDate).slice(0, 5);
                      const shortcutTitle =
                        shortcut.holidayName != null
                          ? `Lands on ${formatUsDateFromIso(shortcut.isoDate)} (${shortcut.holidayName})`
                          : undefined;
                      return (
                        <button
                          key={shortcut.weeks}
                          type="button"
                          title={shortcutTitle}
                          onClick={() => pickWeekShortcut(shortcut.weeks)}
                          className={cn(
                            "rounded-md border px-2 py-1.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500",
                            shortcut.holidayName != null
                              ? "border-amber-200 bg-amber-50 hover:bg-amber-100"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100",
                          )}
                        >
                          <span className="block text-xs font-medium text-slate-700">
                            {shortcut.weeks} {shortcut.weeks === 1 ? "week" : "weeks"}
                          </span>
                          {shortcut.holidayName != null ? (
                            <span className="mt-0.5 block text-[10px] font-medium leading-tight text-amber-800 tabular-nums">
                              {datePreview} · holiday
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

const numberFormatter = new Intl.NumberFormat();

function ButtonIcon(props: { label: string; onClick: () => void; children: ReactNode }): JSX.Element {
  return (
    <button
      type="button"
      aria-label={props.label}
      onClick={props.onClick}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
    >
      {props.children}
    </button>
  );
}
